import {afterNextRender, Component, computed, DestroyRef, ElementRef, inject, Input, signal, Signal, ViewChild} from '@angular/core';
import {MatSidenav, MatSidenavContainer, MatSidenavModule} from '@angular/material/sidenav';
import {MatButton, MatFabButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';
import {BreakpointObserver} from '@angular/cdk/layout';
import {toSignal} from '@angular/core/rxjs-interop';
import {map} from 'rxjs/operators';
import {CurrentObjectContext} from '../current-object.context';
import {CdkPortalOutlet, TemplatePortal} from '@angular/cdk/portal';
import {ResizableDirective} from '../../resizable.directive';
import {ViewportGaps, viewportGapsAround} from './viewport-gaps';
import {CloseOnBack} from './close-on-back';

@Component({
    selector: 'app-object-page-layout',
    imports: [
        MatSidenavModule,
        CdkPortalOutlet,
        MatFabButton,
        MatButton,
        MatIcon,
        TranslateModule,
        ResizableDirective,
    ],
    templateUrl: './object-page-layout.component.html',
    styleUrl: './object-page-layout.component.scss',
    host: {
      '[style.--object-page-drawer-top.px]': 'backdropGaps().top',
      '[style.--object-page-drawer-bottom.px]': 'backdropGaps().bottom',
    },
})
export class ObjectPageLayoutComponent {
  // Injections
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private readonly currentObjectContext: CurrentObjectContext = inject(CurrentObjectContext);
  private readonly hostElement: HTMLElement = inject(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);

  @Input() disableNavigation: boolean = false;
  @Input() disableRightDrawer: boolean = false;
  @Input() defaultImageDrawerWidth?: string;

  @ViewChild('rightDrawer') rightDrawer: MatSidenav;
  @ViewChild('navigationDrawer') navigationDrawer: MatSidenav;
  @ViewChild('matSidenavContainer') matSidenavContainer: MatSidenavContainer;

  readonly isMobile = toSignal(
    this.breakpointObserver.observe('(max-width: 600px)').pipe(map(result => result.matches)),
    {initialValue: false}
  );
  readonly sectionNavigation = signal<TemplatePortal | null>(null);

  isSmallScreen: Signal<boolean>
  rightDrawerOpened = signal(false);
  /** Keeps viewport-fixed mobile drawers inside this page, between the app header and footer. */
  readonly drawerGaps = signal<ViewportGaps>({top: 0, bottom: 0});
  /** The image drawer is full screen on small screens, so its backdrop covers the app header and footer too. */
  readonly backdropGaps = computed<ViewportGaps>(() =>
    this.rightDrawerOpened() && this.isSmallScreen() ? {top: 0, bottom: 0} : this.drawerGaps()
  );
  /** On small screens the image drawer covers the page, so the back button should close it. */
  private readonly rightDrawerCloseOnBack = new CloseOnBack(window, () => this.rightDrawer?.close());

  constructor() {
    this.isSmallScreen = toSignal(this.breakpointObserver.observe('(max-width: 1024px)').pipe(map(result => result.matches)));

    afterNextRender(() => {
      this.matSidenavContainer?.updateContentMargins();
      this.trackDrawerGaps();
    });
    this.destroyRef.onDestroy(() => this.rightDrawerCloseOnBack.dispose());
  }

  get isEditing() {
    return this.currentObjectContext.isEditing;
  }

  onRightDrawerOpenedChange(opened: boolean) {
    this.rightDrawerOpened.set(opened);
    if (opened && this.isSmallScreen()) {
      this.rightDrawerCloseOnBack.opened();
    } else if (!opened) {
      this.rightDrawerCloseOnBack.closed();
    }
  }

  toggleRightDrawer() {
    this.rightDrawer?.toggle();
  }

  toggleNavigationDrawer() {
    this.navigationDrawer?.toggle(undefined, 'program')
  }

  private trackDrawerGaps() {
    const update = () => this.drawerGaps.set(
      viewportGapsAround(this.hostElement.getBoundingClientRect(), window.innerHeight)
    );
    update();
    window.addEventListener('resize', update);
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    resizeObserver?.observe(this.hostElement);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    });
  }

  setRightDrawerWidth(width: string) {
    // @ts-ignore
    this.rightDrawer?._elementRef?.nativeElement?.style.setProperty('width', width);
    this.matSidenavContainer?.updateContentMargins();
  }
}
